// ============================================================================
// Módulo PERSONAL — organización de turnos de trabajadores (v1, jul-2026).
// Se enciende desde Depuración (staff_module_enabled).
//
// Vista semanal: filas = trabajadores de la sede (o transversales), columnas =
// días. Click en una celda → crear turno; click en un turno → editar/eliminar.
// Escritura solo para roles de gestión (el backend también lo valida).
// ============================================================================
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { CalendarClock, ChevronLeft, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import { useBranch } from '../context/branch-context';
import { useUser } from '../context/user-context';
import { ModuleHeader } from './ui/module-header';
import { useTextos } from '../context/regional-context';
import { textosPersonal } from '../../i18n/modulos/personal';
import { localeFechas } from '../../i18n/actual';
import {
  listTrabajadores, listTurnos, crearTurno, actualizarTurno, eliminarTurno,
  type Trabajador, type Turno,
} from '../../api/turnos';

const GESTIONA = new Set(['gerente_administrativo', 'gerente_operativo', 'jefe_operaciones', 'jefe_administrativo', 'administrador', 'desarrollador']);

const inputStyle: CSSProperties = {
  padding: '10px 12px', background: 'var(--surface)', border: '1.5px solid var(--line-2)',
  borderRadius: 'var(--r-sm)', fontSize: 14, fontWeight: 500, color: 'var(--ink)', fontFamily: 'inherit',
};

// Claves (no textos): se traducen en el render.
const DIAS = ['diaLun', 'diaMar', 'diaMie', 'diaJue', 'diaVie', 'diaSab', 'diaDom'] as const;

function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Lunes de la semana que contiene `d`. */
function lunesDe(d: Date): Date {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = lunes
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

const hhmm = (t: string) => String(t).slice(0, 5);

interface ModalState {
  turno?: Turno;          // presente = editar
  usuarioId: number;
  fecha: string;
}

export function Staff() {
  const { currentBranch, currentBranchId } = useBranch();
  const { user } = useUser();
  const puedeGestionar = GESTIONA.has(user?.role || '');
  const tx = useTextos(textosPersonal);

  const [lunes, setLunes] = useState<Date>(() => lunesDe(new Date()));
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [cargando, setCargando] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  const dias = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(lunes);
      d.setDate(lunes.getDate() + i);
      return d;
    }), [lunes]);

  const cargar = () => {
    if (!currentBranchId) return;
    setCargando(true);
    setLoadError(null);
    Promise.all([
      listTrabajadores(),
      listTurnos({ sede_id: currentBranchId, desde: fmtISO(dias[0]), hasta: fmtISO(dias[6]) }),
    ])
      .then(([ts, tn]) => {
        // TODOS los trabajadores activos, ordenados: primero los de esta sede
        // y los transversales (sede_id null), luego los de otras sedes. Antes
        // se filtraba por sede y una sede sin usuarios asignados quedaba
        // VACÍA sin forma de crear turnos (bug jul-2026).
        const orden = (t: Trabajador) => (t.sede_id == null || t.sede_id === currentBranchId) ? 0 : 1;
        setTrabajadores([...ts].sort((a, b) => orden(a) - orden(b) || a.nombre.localeCompare(b.nombre, 'es')));
        setTurnos(tn);
      })
      .catch((err: any) => {
        // Mostrar el error en pantalla — tragarlo dejaba el módulo "muerto"
        // sin explicación (ej. API sin reiniciar → 404 en /api/turnos).
        const status = err?.status ? ` (HTTP ${err.status})` : '';
        setLoadError(
          `${err?.message || tx('errorCargando')}${status}.` +
          (err?.status === 404 ? tx('reiniciasteApi') : '')
        );
      })
      .finally(() => setCargando(false));
  };
  useEffect(cargar, [currentBranchId, lunes]);

  const turnosDe = (usuarioId: number, fecha: string) =>
    turnos.filter(t => t.usuario_id === usuarioId && t.fecha === fecha);

  const hoy = fmtISO(new Date());

  return (
    <div className="pb-32 max-w-screen-2xl mx-auto">
      <ModuleHeader
        title={tx('titulo')}
        icon={CalendarClock}
        stats={[
          { n: trabajadores.length, label: tx('trabajadores'), tone: 'blue' },
          { n: turnos.length, label: tx('turnosEstaSemana') },
        ]}
        below={
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => setLunes(l => { const d = new Date(l); d.setDate(d.getDate() - 7); return d; })}
              className="press" style={{ ...inputStyle, borderRadius: 999, padding: '7px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ChevronLeft className="w-4 h-4" /> {tx('semanaAnterior')}
            </button>
            <b style={{ fontSize: 14.5, color: 'var(--ink)' }}>
              {dias[0].toLocaleDateString(localeFechas(), { timeZone: 'America/Bogota',  day: 'numeric', month: 'short' })} — {dias[6].toLocaleDateString(localeFechas(), { timeZone: 'America/Bogota',  day: 'numeric', month: 'short', year: 'numeric' })}
            </b>
            <button type="button" onClick={() => setLunes(l => { const d = new Date(l); d.setDate(d.getDate() + 7); return d; })}
              className="press" style={{ ...inputStyle, borderRadius: 999, padding: '7px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {tx('semanaSiguiente')} <ChevronRight className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setLunes(lunesDe(new Date()))}
              className="press" style={{ ...inputStyle, borderRadius: 999, padding: '7px 12px', cursor: 'pointer' }}>
              {tx('hoy')}
            </button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8">
        <p style={{ color: 'var(--muted-ink)', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          {tx('turnosDeLaSemana')} <b style={{ color: 'var(--ink)' }}>{currentBranch}</b>
          {!puedeGestionar && tx('soloLectura')}
        </p>

        {loadError && (
          <div style={{
            padding: '12px 16px', marginBottom: 14, borderRadius: 'var(--r-md)',
            background: 'rgba(220,38,38,.07)', border: '1.5px solid rgba(220,38,38,.35)',
            color: '#b91c1c', fontSize: 13.5, fontWeight: 700,
          }}>
            ⚠️ {loadError}
          </div>
        )}
        {cargando ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--muted-ink)', fontWeight: 600 }}>
            {tx('cargandoTurnos')}
          </div>
        ) : (
          <div className="tablecard">
            <div className="tablescroll">
              <table className="cmp" style={{ minWidth: 980 }}>
                <thead>
                  <tr>
                    <th>{tx('trabajador')}</th>
                    {dias.map((d, i) => (
                      <th key={i} style={{ textAlign: 'center', background: fmtISO(d) === hoy ? 'var(--blue-50, #eff6ff)' : undefined }}>
                        {tx(DIAS[i])} {d.getDate()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trabajadores.map(t => (
                    <tr key={t.id}>
                      <td>
                        <b>{t.nombre}</b>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--muted-ink)' }}>{t.rol}</div>
                      </td>
                      {dias.map((d, i) => {
                        const fecha = fmtISO(d);
                        const lista = turnosDe(t.id, fecha);
                        return (
                          <td key={i} style={{ verticalAlign: 'top', textAlign: 'center', whiteSpace: 'normal', background: fecha === hoy ? 'var(--blue-50, #eff6ff)' : undefined }}>
                            <div className="flex flex-col items-center gap-1">
                              {lista.map(turno => (
                                <button key={turno.id} type="button"
                                  onClick={() => puedeGestionar && setModal({ turno, usuarioId: t.id, fecha })}
                                  title={turno.puesto || undefined}
                                  style={{
                                    padding: '4px 8px', borderRadius: 8, border: 0, cursor: puedeGestionar ? 'pointer' : 'default',
                                    background: 'rgba(31,79,216,.1)', color: 'var(--blue-700, #0D5EAF)',
                                    fontSize: 11.5, fontWeight: 800, fontFamily: 'inherit', whiteSpace: 'nowrap',
                                  }}>
                                  {hhmm(turno.hora_inicio)}–{hhmm(turno.hora_fin)}{turno.puesto ? ` · ${turno.puesto}` : ''}
                                </button>
                              ))}
                              {puedeGestionar && (
                                <button type="button" onClick={() => setModal({ usuarioId: t.id, fecha })}
                                  aria-label={tx('agregarTurno')}
                                  style={{
                                    padding: 3, borderRadius: 6, border: '1px dashed var(--line-2)', cursor: 'pointer',
                                    background: 'transparent', color: 'var(--muted-2, #94a3b8)', lineHeight: 0,
                                  }}>
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {trabajadores.length === 0 && !loadError && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted-ink)', fontWeight: 600 }}>
                      {tx('sinTrabajadores')} <b>{tx('moduloUsuarios')}</b> {tx('apareceranAca')}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modal && currentBranchId && (
        <TurnoModal
          state={modal}
          sedeId={currentBranchId}
          trabajador={trabajadores.find(t => t.id === modal.usuarioId)?.nombre || ''}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); cargar(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Modal crear/editar turno.
// ============================================================================
function TurnoModal({ state, sedeId, trabajador, onClose, onSaved }: {
  state: ModalState;
  sedeId: number;
  trabajador: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editando = !!state.turno;
  const tx = useTextos(textosPersonal);
  const [horaInicio, setHoraInicio] = useState(state.turno ? hhmm(state.turno.hora_inicio) : '08:00');
  const [horaFin, setHoraFin] = useState(state.turno ? hhmm(state.turno.hora_fin) : '16:00');
  const [puesto, setPuesto] = useState(state.turno?.puesto || '');
  const [notas, setNotas] = useState(state.turno?.notas || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    if (horaFin <= horaInicio) { setError(tx('horaFinMayor')); return; }
    setBusy(true); setError(null);
    try {
      if (editando) {
        await actualizarTurno(state.turno!.id, { hora_inicio: horaInicio, hora_fin: horaFin, puesto: puesto.trim() || null, notas: notas.trim() || null });
      } else {
        await crearTurno({ sede_id: sedeId, usuario_id: state.usuarioId, fecha: state.fecha, hora_inicio: horaInicio, hora_fin: horaFin, puesto: puesto.trim() || null, notas: notas.trim() || null });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.message || tx('noSeGuardo'));
      setBusy(false);
    }
  };

  const borrar = async () => {
    if (!editando) return;
    setBusy(true); setError(null);
    try { await eliminarTurno(state.turno!.id); onSaved(); }
    catch (e: any) { setError(e?.message || tx('noSeElimino')); setBusy(false); }
  };

  return (
    <div onClick={onClose}
      className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-apple-fade-in"
      style={{ background: 'rgba(14,22,38,.55)', backdropFilter: 'blur(10px)' }}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md flex flex-col animate-apple-slide-up overflow-hidden sk-modal-shell"
        style={{ background: 'var(--surface)', boxShadow: 'var(--sh-lg)' }}>
        <div className="shrink-0 px-5 pt-5 pb-4 flex items-start justify-between gap-3" style={{ borderBottom: '1.5px solid var(--line)' }}>
          <div>
            <h2 style={{ color: 'var(--ink)', fontSize: 19, fontWeight: 800 }}>
              {editando ? tx('editarTurno') : tx('nuevoTurno')}
            </h2>
            <p className="mt-1" style={{ color: 'var(--muted-ink)', fontSize: 13, fontWeight: 600 }}>
              {trabajador} · {new Date(state.fecha + 'T12:00:00').toLocaleDateString(localeFechas(), { timeZone: 'America/Bogota',  weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 press" aria-label={tx('cerrar')}
            style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', color: 'var(--muted-ink)', border: 0, cursor: 'pointer' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div style={{ padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'rgba(220,38,38,.08)', border: '1.5px solid rgba(220,38,38,.35)', color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sk-label block mb-1">{tx('horaInicio')}</label>
              <input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)}
                className="w-full focus:outline-none" style={inputStyle} disabled={busy} />
            </div>
            <div>
              <label className="sk-label block mb-1">{tx('horaFin')}</label>
              <input type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)}
                className="w-full focus:outline-none" style={inputStyle} disabled={busy} />
            </div>
          </div>
          <div>
            <label className="sk-label block mb-1">{tx('puestoOpcional')}</label>
            <input value={puesto} onChange={(e) => setPuesto(e.target.value)} placeholder={tx('puestoPlaceholder')}
              className="w-full focus:outline-none" style={inputStyle} disabled={busy} />
          </div>
          <div>
            <label className="sk-label block mb-1">{tx('notasOpcional')}</label>
            <input value={notas} onChange={(e) => setNotas(e.target.value)}
              className="w-full focus:outline-none" style={inputStyle} disabled={busy} />
          </div>
        </div>

        <div className="shrink-0 px-5 py-3 flex justify-between gap-2" style={{ background: 'var(--surface-2)', borderTop: '1.5px solid var(--line)' }}>
          {editando ? (
            <button type="button" onClick={borrar} disabled={busy} className="sk-btn sk-btn--ghost"
              style={{ minHeight: 44, padding: '10px 16px', fontSize: 14, color: '#b91c1c' }}>
              <Trash2 className="w-4 h-4" /> {tx('eliminar')}
            </button>
          ) : <span />}
          <button type="button" onClick={guardar} disabled={busy} className="sk-btn sk-btn--primary"
            style={{ minHeight: 44, padding: '10px 22px', fontSize: 14 }}>
            {busy ? tx('guardando') : editando ? tx('guardarCambios') : tx('crearTurno')}
          </button>
        </div>
      </div>
    </div>
  );
}
